import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import {
  type WorkflowCaseTag,
  WorkflowCaseTagSchema,
  type WorkflowTagPoolEntry,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  BorderRadius,
  Box,
  HStack,
  Input,
  LightModeSemanticColors,
  SecondaryButton,
  TagCloseTrigger,
  TagLabel,
  TagRoot,
  TagStartElement,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { ToggleButton } from '~/components/common/ToggleButton'
import { isEnterKey } from '~/libs/keyboard'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import {
  WORKFLOW_CASE_TAG_COLORS,
  WORKFLOW_CASE_TAG_COLOR_VALUES,
  WORKFLOW_CASE_TAG_DEFAULT_COLOR,
  WORKFLOW_CASE_TAG_LABEL_MAX_LENGTH,
  WORKFLOW_CASE_TAG_MAX_COUNT,
  WORKFLOW_CASE_TAG_POOL_MAX_COUNT,
  type WorkflowCaseTagColorValue,
  getWorkflowCaseTagColorStyle,
  getWorkflowCaseTagHoverBackground,
  isDuplicateWorkflowCaseTagLabel,
  normalizeWorkflowCaseTagLabel,
} from '~/models/workflowCaseTag'

type Props = {
  tags: WorkflowCaseTag[]
  /** 追加 / 削除後の完全なタグ配列。caller が即時保存するか local state 更新に使うかを決める。 */
  onChange: (next: WorkflowCaseTag[]) => void
  disabled?: boolean
  /**
   * 組織全体のタグプール (backend `ListWorkflowTagPool` RPC の全件、spec
   * AC-TAG-005)。design `.tag-suggest` = 「作成済のタグ」再利用サジェストの
   * 候補元として、現在の `tags` に無い label のものだけを表示する (渡さない /
   * 候補が空になる場合はパネル自体を表示しない)。各 entry は参照件数
   * (`referenceCount`) と削除可否 (`deletable`) を持つ (spec AC-TAG-009,
   * workflow-tag-delete-ui design Decision B/F)。
   *
   * 新規タグ作成時の 20 個上限事前チェック (AC-TAG-006) の基準集合も兼ねる:
   * 追加しようとする label がこのプールに無い (= 新規 label) かつ
   * `suggestedTags.length >= 20` の場合、追加を拒否する。プール未取得
   * (undefined) の間はチェックを省略する (backend が最終的な正)。
   */
  suggestedTags?: WorkflowTagPoolEntry[]
  /**
   * 削除予定タグ label の集合 (controlled、spec AC-TAG-009)。渡された時のみ
   * 「作成済のタグ」ボックスに編集/完了トグル + 削除可チップの × を表示する
   * (Decision E: 削除予定 state はこのコンポーネントの編集モード UI が
   * 内包するが、値は caller が保持し保存/破棄を決める controlled 値として
   * 公開する)。省略時は従来通りサジェスト専用 (クリックで追加のみ、編集
   * モードなし) — 雛形の新規作成フォームなど `removed_tag_labels` を持たない
   * 保存経路で使う。
   */
  removedTagLabels?: string[]
  onRemovedTagLabelsChange?: (next: string[]) => void
  /**
   * 名称入力欄の幅上限。design は画面ごとに正が異なるためコンポーネント内で一律に決められない:
   * 雛形編集は `case-templates.html:140` の `.ct-tag-input` が `max-width:280px`、
   * ワークフロー追加モーダル / ケース詳細は `cases.html:112` / `case-detail.html:49` の
   * `.tag-input` に上限が無い。未指定 (既定) は上限なし。
   */
  inputMaxWidth?: string
  /**
   * 雛形編集の見出し (design `case-templates.html` `.ct-tag-set`、 #4547)。 true の時、
   * 設定済みのタグの上に見出し「この雛形のタグ」 (0 件なら案内文) を出し、 「作成済のタグ」
   * の見出しに「クリックでこの雛形に追加」 を添える。
   *
   * 設定済みのタグと「作成済のタグ」 (再利用候補) が同じ形のチップで並び、 どちらが
   * この雛形に付いているのか初見で混同された (ドッグフーディング 2026-08-03)。 両方に
   * 見出しを付けて役割を対で示す。 0 件でも見出しを残すのは、 見出しが消えると候補側が
   * 設定済みに見えて同じ混同が起きるため。 WF 詳細・新規作成ダイアログの design には
   * 無いので、 雛形編集だけが渡す。
   */
  templateHeadings?: boolean
}

/**
 * タグの追加・削除を行う共通エディタ (design `.tag-editor` 相当)。
 *
 * WorkflowCaseDetail (AC-06: 即時保存) / AdminWorkflowTemplates の雛形編集フォーム
 * (AC-10: デフォルトタグ、フォーム送信まで保存しない) / CreateWorkflowCasesDialog
 * (AC-04/05: 作成時タグ) の 3 箇所で共有する。「即時保存するか」は呼び出し側が
 * `onChange` の実装で決める (controlled component)。
 *
 * AC-02/03/04 (トリム・空文字拒否・重複拒否・上限 10) は UX 目的の事前チェックであり、
 * 業務判断の正は backend (`valueobject.NewWorkflowCaseTag` / `WithUpdatedTags`)。
 */
export const WorkflowCaseTagEditor = ({
  tags,
  onChange,
  disabled = false,
  suggestedTags,
  removedTagLabels,
  onRemovedTagLabelsChange,
  inputMaxWidth,
  templateHeadings = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [labelInput, setLabelInput] = useState('')
  const [selectedColor, setSelectedColor] = useState<WorkflowCaseTagColorValue>(
    WORKFLOW_CASE_TAG_DEFAULT_COLOR,
  )
  const [error, setError] = useState<string | undefined>(undefined)
  // AC-TAG-009 (workflow-tag-delete-ui design): 「作成済のタグ」ボックスの
  // 編集/完了トグル。渡された時だけ有効 (removedTagLabels /
  // onRemovedTagLabelsChange の両方が Some の caller のみ)。
  const [editMode, setEditMode] = useState(false)
  const canEditPool =
    removedTagLabels !== undefined && onRemovedTagLabelsChange !== undefined

  const isMaxReached = tags.length >= WORKFLOW_CASE_TAG_MAX_COUNT
  // 押せない理由 (AC-TAG-004): 非活性の条件と画面上の文を同じ値から導く。
  const maxReachedReason = isMaxReached
    ? t('common:tag.maxReachedTooltip')
    : undefined
  const isAddDisabled = disabled || isMaxReached
  // design case-templates.html: 入力が空のあいだ「追加」ボタンは disabled (id="ct-tag-add" 初期 disabled属性 +
  // input の input イベントで toggle)。
  const isAddButtonDisabled =
    isAddDisabled || normalizeWorkflowCaseTagLabel(labelInput) === ''

  const addTag = (label: string, color: string): void => {
    onChange([...tags, create(WorkflowCaseTagSchema, { label, color })])
    // AC-TAG-009 (Decision D の不変条件復元): 削除予定は「現在の WF/雛形に
    // 未付与のプールタグ」だけが対象。ここで付与すると当該 label は付与済みに
    // なるため、削除予定に残っていると保存時に backend の removed 優先で無言に
    // 剥がされる (silent void)。付与と同時に削除予定から外して整合させる。
    if (
      removedTagLabels !== undefined &&
      onRemovedTagLabelsChange !== undefined &&
      removedTagLabels.includes(label)
    ) {
      onRemovedTagLabelsChange(removedTagLabels.filter((l) => l !== label))
    }
  }

  /**
   * 「いま追加を押したらどうなるか」 を返す純関数。
   *
   * `handleAdd` のガードをそのまま持ち、 **エラー文言を出す側と、 プレビューを
   * 出す側の両方がここだけを見る**。 コピーして 2 箇所に置くと、 片方だけ直った
   * 時に「プレビューは出ているのに追加できない」 という嘘の予告になる
   * (2026-08-26 デザイナー要望でプレビューを足すにあたっての不変条件)。
   *
   * 戻り値が `'ok'` の時だけプレビューを出す = **見えていれば必ずその形で入る**。
   */
  const evaluateAddability = (
    label: string,
  ): 'ok' | 'disabled' | 'empty' | 'tooLong' | 'duplicate' | 'poolLimit' => {
    if (isAddDisabled) return 'disabled'
    if (label === '') return 'empty'
    if (label.length > WORKFLOW_CASE_TAG_LABEL_MAX_LENGTH) return 'tooLong'
    if (isDuplicateWorkflowCaseTagLabel(tags, label)) return 'duplicate'
    // AC-TAG-006 (Decision #1): プール未収録の新規 label を追加しようと
    // していて、かつ「現在のプール + このセッションで既に追加した (プール
    // 未取得時点では見えない) 新規 label 数」が 20 個上限に達している場合は
    // 拒否する (backend `ensureWorkflowCaseTagPoolCapacity` の
    // `現在プール + distinct 新 label > 20` と同じ判定式)。`suggestedTags`
    // は取得時点の静的スナップショットなので、この同一セッション内で連続
    // 追加した「プール未収録の新 label」を実効プールサイズへ都度加算しないと、
    // 2 個目以降の新規追加が事前チェックをすり抜ける。プール未取得
    // (suggestedTags === undefined) の間はこの事前チェックを省略し、
    // backend の最終判定に委ねる。
    if (suggestedTags !== undefined) {
      const isNewToPool = !isDuplicateWorkflowCaseTagLabel(suggestedTags, label)
      const newLabelsAlreadyInSession = tags.filter(
        (existingTag) =>
          !isDuplicateWorkflowCaseTagLabel(suggestedTags, existingTag.label),
      ).length
      // AC-TAG-009: 削除予定にしたプールタグは同一保存 tx で backend が capacity
      // guard より前に剥がすため実効プールから外れる (`removed_tag_labels` は
      // 剥がし → 適用 → 上限判定 の順)。新文言「整理は編集モードから行えます」の
      // 案内どおり編集モードで整理した分を実効プールから差し引かないと、backend が
      // 通す保存を frontend の事前チェックが誤って拒否する。
      const pendingRemovalFromPool = (removedTagLabels ?? []).filter((l) =>
        isDuplicateWorkflowCaseTagLabel(suggestedTags, l),
      ).length
      const effectivePoolSize =
        suggestedTags.length +
        newLabelsAlreadyInSession -
        pendingRemovalFromPool
      if (
        isNewToPool &&
        effectivePoolSize >= WORKFLOW_CASE_TAG_POOL_MAX_COUNT
      ) {
        return 'poolLimit'
      }
    }
    return 'ok'
  }

  const ADD_ERROR_KEY: Record<string, string> = {
    empty: 'common:tag.emptyLabelError',
    tooLong: 'common:tag.maxLengthError',
    duplicate: 'common:tag.duplicateLabelError',
    poolLimit: 'common:tag.poolLimitError',
  }

  const handleAdd = (): void => {
    const label = normalizeWorkflowCaseTagLabel(labelInput)
    const verdict = evaluateAddability(label)
    if (verdict === 'disabled') return
    if (verdict !== 'ok') {
      setError(t(ADD_ERROR_KEY[verdict] ?? 'common:tag.emptyLabelError'))
      return
    }
    setError(undefined)
    setLabelInput('')
    addTag(label, selectedColor)
  }

  const handleRemove = (idx: number): void => {
    if (disabled) return
    onChange(tags.filter((_, i) => i !== idx))
  }

  // AC-05 拡張: 作成済のタグ (他の雛形/ケースで保存済) をクリックで再利用。 色も引き継ぐ
  // (design cases.html AddExistingTag 準拠)。 重複防止は poolTags のフィルタで担保済みだが
  // 二重防御として isDuplicateWorkflowCaseTagLabel も見る。
  const handleAddSuggested = (suggested: WorkflowTagPoolEntry): void => {
    if (isAddDisabled) return
    if (isDuplicateWorkflowCaseTagLabel(tags, suggested.label)) return
    addTag(suggested.label, suggested.color)
  }

  // AC-TAG-009: 編集モードで削除可チップの × を押すと、その label を削除予定
  // 集合に追加/解除する (トグル)。即時削除はしない — caller が保存時に
  // `removed_tag_labels` として送出する (Decision C/E)。
  const togglePendingRemoval = (label: string): void => {
    if (
      removedTagLabels === undefined ||
      onRemovedTagLabelsChange === undefined
    )
      return
    if (removedTagLabels.includes(label)) {
      onRemovedTagLabelsChange(removedTagLabels.filter((l) => l !== label))
    } else {
      onRemovedTagLabelsChange([...removedTagLabels, label])
    }
  }

  // AC-TAG-008: 現在の tags に無い label のみ候補にする (二重付与防止) +
  // 入力文字列で部分一致フィルタ (大文字小文字区別なし)。入力が空 (trim 後)
  // のときは全候補を出す (= design `_allTemplateTags` の既定挙動)。
  const trimmedFilter = labelInput.trim().toLowerCase()
  const poolTags = (suggestedTags ?? []).filter((p) => {
    if (isDuplicateWorkflowCaseTagLabel(tags, p.label)) return false
    if (trimmedFilter === '') return true
    // 英語表示では初期のタグを英語で出すので、 表示名でも比べる (#11795)
    return (
      p.label.toLowerCase().includes(trimmedFilter) ||
      localizeBuiltinWorkflowText(p.label, t)
        .toLowerCase()
        .includes(trimmedFilter)
    )
  })

  // プレビュー: `evaluateAddability` が 'ok' の時だけ出す
  // (= 見えていれば必ずその形で入る)。 表示するのは正規化**後**の文字列で、
  // 前後空白を落とした「実際に入る形」 を見せる。
  const normalizedPreviewLabel = normalizeWorkflowCaseTagLabel(labelInput)
  const previewLabel =
    evaluateAddability(normalizedPreviewLabel) === 'ok'
      ? normalizedPreviewLabel
      : undefined
  const previewStyle = getWorkflowCaseTagColorStyle(
    selectedColor,
    LightModeSemanticColors.Surface.primary.value,
    LightModeSemanticColors.Text.highEmphasis.value,
  )

  // design .ct-tag-editor-main: 名称 input + 追加ボタン (1 行目)。
  const addRow = (
    <HStack gap={2} width="100%">
      {/* design .ct-tag-input / .tag-input: flex:1 + min-width:140px。 上限は画面ごとに
          正が異なるため `inputMaxWidth` で呼び出し側から与える (雛形編集のみ 280px)。
          ecloud Input は chakra-field__root の wrapper div に包まれ style props が内側
          input にしか効かないため、 幅制約は wrapper ごと Box で与える。
          文字サイズは size では決まらない: chakra の input recipe は size=md でも field へ
          `textStyle: 'sm'` を渡し、 本 app の system では ecloud の sm (12px) に解決される。
          design `.ecl-input--sm` は font-size 14px (components.css:2801 + tokens.css:173 の
          `--font-size-md`) なので `textStyle="md"` を明示する (EnumSelect.tsx と同じ対処)。 */}
      <Box flex="1" minWidth="140px" maxWidth={inputMaxWidth}>
        <Input
          textStyle="md"
          width="100%"
          aria-label={t('common:tag.addLabelPlaceholder')}
          disabled={isAddDisabled}
          maxLength={WORKFLOW_CASE_TAG_LABEL_MAX_LENGTH}
          onChange={(e) => {
            setLabelInput(e.target.value)
          }}
          onKeyDown={(e) => {
            if (isEnterKey(e)) {
              e.preventDefault()
              handleAdd()
            }
          }}
          placeholder={t('common:tag.addLabelPlaceholder')}
          size="md"
          value={labelInput}
        />
      </Box>
      {/* 入力欄は design の `.ecl-input--sm` (= 40px、 components.css:2801) に合わせて `md`。
          追加ボタンも `md`: design は cases.html:464-465 で「追加ボタンは隣の input
          (ecl-input--sm=40px) に高さを揃える」と明示し、 cases.html:473 /
          case-templates.html:1251 とも `btn btn-secondary` (= `.btn` 既定 40px、
          components.css:24) で `btn-sm` を付けていない。 入力欄 (40px) と横に並ぶ 1 行の
          部品なので高さを揃える。 */}
      <SecondaryButton
        disabled={isAddButtonDisabled}
        onClick={handleAdd}
        size="md"
      >
        {t('common:tag.add')}
      </SecondaryButton>
    </HStack>
  )

  // 設定済みのタグ (design `.ct-tag-list`)。 雛形編集では見出しの下に置く ([[Props.templateHeadings]])。
  const selectedChips = (
    <Wrap gap={1}>
      {tags.map((tag, idx) => {
        const tagStyle = getWorkflowCaseTagColorStyle(
          tag.color,
          LightModeSemanticColors.Surface.primary.value,
          LightModeSemanticColors.Text.highEmphasis.value,
        )
        return (
          <WrapItem key={tag.label}>
            {/* [design 差異] DS Tag md の文字は sm (12px) だが、 タグ名が
                読みづらいというデザイナー指摘 (2026-08-17) で本文と同じ md
                (14px) にする。 8080 プロト側も同時に 14px へ更新済 (design-desk)。
                geometry (高さ 24px / padding) は DS の md のままにして、
                文字サイズだけ上げる。
                `fontSize` ではなく `textStyle` を使う: tag recipe は size=md でも
                `textStyle: 'sm'` を渡すため、 fontSize だけ上書きすると lineHeight が
                sm のまま残り、 14px の文字を 12px 相当の行高で描いて 高さ 24px 固定と
                衝突する。 同ファイルの Input (textStyle="md") と対処を揃える。 */}
            <TagRoot
              // design `.case-tag` は `--radius-full` (pill)。 ステータス Badge
              // (角丸小) と字面で区別するための意図的な差 (2026-08-17 デザイナー指摘)。
              textStyle="md"
              borderRadius="full"
              size="md"
              style={tagStyle}
              variant="subtle"
            >
              {/* 文字サイズは label スロットに当てる。 root への textStyle は
                  slot recipe の label 側指定に負けて 10px のまま残っていた
                  (2026-08-17 デザイナー実測)。 */}
              <TagLabel textStyle="md">
                {localizeBuiltinWorkflowText(tag.label, t)}
              </TagLabel>
              {!disabled && (
                <TagCloseTrigger
                  boxSize="14px"
                  opacity={0.75}
                  // [DS差異] ecloud-designsystem 0.14.2 の tag slot recipe (tag.recipe.ts) は
                  // root スロットのみサイズ定義しており closeTrigger スロットのスタイルが
                  // 欠落している (node_modules/@enechain/ecloud-designsystem 実物確認済み)。
                  // 未指定のままだと closeTrigger が 0x0 で描画され見えず押せない (P0 バグ)。
                  // 明示スタイルで可視化する workaround。 design cases.html `.case-tag-x`
                  // (中立色・チップ文字色 inherit・opacity .75、hover で 1) に準拠。
                  // ⚠ hover は**このコメントが主張していたのに実装されていなかった**
                  // (2026-09-16 デザイナー指摘「タグにホバーを反映したい」)。
                  // 8080 `demand/cases.html:103` の `.case-tag-x:hover { opacity: 1 }`。
                  _hover={{ opacity: 1 }}
                  cursor="pointer"
                  aria-label={t('common:tag.removeAriaLabel', {
                    label: localizeBuiltinWorkflowText(tag.label, t),
                  })}
                  css={{ '& svg': { width: '12px', height: '12px' } }}
                  onClick={() => {
                    handleRemove(idx)
                  }}
                />
              )}
            </TagRoot>
          </WrapItem>
        )
      })}
    </Wrap>
  )

  return (
    <VStack align="stretch" gap={2} width="100%">
      {templateHeadings ? (
        <VStack align="stretch" gap={2}>
          <Text textStyle="label">
            {t('common:tag.templateSelectedHeading')}
          </Text>
          {tags.length > 0 ? (
            selectedChips
          ) : (
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('common:tag.templateSelectedEmpty')}
            </Text>
          )}
        </VStack>
      ) : (
        tags.length > 0 && selectedChips
      )}

      {maxReachedReason !== undefined ? (
        <Tooltip content={maxReachedReason}>
          <Box width="100%">{addRow}</Box>
        </Tooltip>
      ) : (
        addRow
      )}
      {/* 入力行の直下に右端をそろえて理由を常時表示 (tooltip は補助) */}
      {maxReachedReason !== undefined && (
        <ActionReasonText textAlign="right">
          {maxReachedReason}
        </ActionReasonText>
      )}
      {/* 追加後の姿のプレビュー (2026-08-26 デザイナー要望)。
          「追加ボタンを押す前に最終形を確認できると良い」。

          ## 置き場所を input の直下にした理由
          確定済み chip の列 (上) に混ぜると「もう追加された」 と誤読される。
          先頭に「追加後」 の語を置き、 列とは別行にすることで
          **まだ入っていない**ことを字と位置の両方で示す。
          8080 プロトには相当実装が無く (3 画面とも入力中は追加ボタンの disabled を
          切り替えるだけ)、 準拠先が無いため本配置は新規の Des 判断。

          ## error と同じスロットを排他共有する
          行を増やさないため。 プレビューが出る条件は `evaluateAddability === 'ok'`、
          error が出るのは追加を押して失敗した後なので両立しない。

          ## 見た目は確定 chip と同一経路
          上の Wrap と同じ `TagRoot`/`TagLabel` + `getWorkflowCaseTagColorStyle` を通す。
          別部品 (`WorkflowCaseTagChip` = ChakraBadge) を使うと recipe が違って
          「確認したのに確定後と違う」 になるため使わない。 差分は × を出さないことだけ。

          ## a11y
          入力中の文字はスクリーンリーダーが input 側で既に読むので、 ここは
          `aria-hidden` にして二重読み上げを避ける (視覚確認専用)。 */}
      {previewLabel !== undefined ? (
        <HStack gap={2} aria-hidden="true">
          <Text textStyle="label" color="Text.lowEmphasis" whiteSpace="nowrap">
            {t('common:tag.previewLabel')}
          </Text>
          <TagRoot
            textStyle="md"
            borderRadius="full"
            size="md"
            style={previewStyle}
            variant="subtle"
          >
            <TagLabel textStyle="md">
              {localizeBuiltinWorkflowText(previewLabel, t)}
            </TagLabel>
          </TagRoot>
        </HStack>
      ) : (
        error !== undefined && (
          <Text color="Text.negativeMid" fontSize="sm">
            {error}
          </Text>
        )
      )}

      {/* 色スウォッチ (design `.ct-tag-swatch`)。 ecloud に円形カラースウォッチ選択
          primitive が無いため raw button を使用。 */}
      <HStack
        gap={2}
        aria-label={t('common:tag.colorSwatchAriaLabel', { color: '' })}
        role="group"
      >
        {WORKFLOW_CASE_TAG_COLORS.map((c) => {
          const isSelected = selectedColor === c
          return (
            <button
              key={c}
              aria-label={t('common:tag.colorSwatchAriaLabel', { color: c })}
              aria-pressed={isSelected}
              disabled={isAddDisabled}
              onClick={() => {
                setSelectedColor(c)
              }}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: BorderRadius.radiiFull.value,
                background: WORKFLOW_CASE_TAG_COLOR_VALUES[c],
                // design case-templates.html `.ct-tag-swatch`: 淡色でも白背景で見える境界線
                // (border-color でなく box-shadow で選択リングを表現し、border 自体は常時 1px
                // 固定してレイアウトシフトを防ぐ)。
                border: `1px solid color-mix(in srgb, ${LightModeSemanticColors.Text.highEmphasis.value} 25%, transparent)`,
                boxShadow: isSelected
                  ? `0 0 0 2px ${LightModeSemanticColors.Surface.primary.value}, 0 0 0 4px ${LightModeSemanticColors.Text.highEmphasis.value}`
                  : 'none',
                cursor: isAddDisabled ? 'default' : 'pointer',
                padding: 0,
                flex: 'none',
                opacity: isAddDisabled ? 0.5 : 1,
              }}
              type="button"
            />
          )
        })}
      </HStack>

      {/* 作成済のタグ (design cases.html `.tag-suggest` / case-templates.html `.ct-tag-suggest`
          準拠): 雛形/ケース横断で保存済のタグをクリック 1 つで再利用する。 現在の tags と
          重複する label は候補から除外済み (poolTags)。 design (case-templates.html
          renderEdit の tagBlock 組み立て順) ではスウォッチ行の下に位置する。
          AC-TAG-009: canEditPool の時は見出し右に編集/完了トグルを出し、編集モード中は
          削除可 (deletable) チップに × を、削除予定になったチップにはインラインアラート
          (参照件数 > 0 の場合) を表示する (workflow-tag-delete-ui design Decision B/E/F)。 */}
      {(poolTags.length > 0 || (canEditPool && editMode)) && (
        <Box
          background="Surface.secondary"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="md"
          paddingX={3}
          paddingY={2}
        >
          <HStack align="center" justify="space-between" width="100%">
            <HStack align="baseline" gap={2}>
              <Text textStyle="label">{t('common:tag.suggestHeading')}</Text>
              {/* 雛形編集だけ「クリックでこの雛形に追加」 を添える (design
                  `.ct-tag-heading-hint`)。 編集モード中はクリックが削除になるので出さない。 */}
              {templateHeadings && !(canEditPool && editMode) && (
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('common:tag.templateSuggestHint')}
                </Text>
              )}
            </HStack>
            {canEditPool && (
              <ToggleButton
                disabled={disabled}
                onClick={() => {
                  setEditMode((prev) => !prev)
                }}
                pressed={editMode}
                size="sm"
              >
                {editMode
                  ? t('common:tag.editToggleOff')
                  : t('common:tag.editToggleOn')}
              </ToggleButton>
            )}
          </HStack>
          <Wrap gap={1} marginTop={2}>
            {poolTags.map((p) => {
              const poolStyle = getWorkflowCaseTagColorStyle(
                p.color,
                LightModeSemanticColors.Surface.primary.value,
                LightModeSemanticColors.Text.highEmphasis.value,
              )
              const isPendingRemoval =
                removedTagLabels?.includes(p.label) ?? false
              const isEditing = canEditPool && editMode
              // 編集モード中は「削除可」チップだけが × 削除操作の対象になる
              // (組込み雛形寄与タグ = 定期/変更 には × を出さない、Decision F)。
              // 編集モード中はクリックでの追加を無効化する (追加/削除の 2 操作が
              // 同じチップ領域で競合しないようにする)。
              const showDeleteAffordance = isEditing && p.deletable
              // silent-void 防止: 削除予定のチップは編集モードを抜けた後も
              // クリックで追加できてはならない。追加→保存すると backend の
              // 「removed 優先」(overlap semantics) で無言に剥がされ、
              // ユーザーの追加操作が何も起きなかったかのように消える
              // (shipping-feature Gate 指摘)。
              const canAddByClick = !isEditing && !isPendingRemoval
              // **押せる時だけ** hover を出す (DS の思想: hover は押せる要素の印)。
              //
              // ⚠ 背景はインライン `style` に置くと `:hover` 規則に勝ってしまい、
              // `_hover` を足しても色が変わらない。押せるチップに限り背景だけ
              // インラインから外し、chakra の prop 経由 (= クラス) にする。
              // こうすると base も hover も同じ層の規則になり、後勝ちで hover が効く
              // (`!important` を持ち出さずに済む)。
              const { backgroundColor: poolBg, ...poolStyleRest } =
                poolStyle ?? {}
              const poolHoverBg = getWorkflowCaseTagHoverBackground(
                p.color,
                LightModeSemanticColors.Surface.primary.value,
              )
              const poolHover =
                canAddByClick && !isAddDisabled
                  ? {
                      // タグ色があれば 8080 どおり同色を濃く (16% → 22%)。
                      // 未知の色でタグ色を解決できないチップは DS の中立
                      // オーバーレイに落とす (DS `Hover.lowEmphasis`)。
                      backgroundColor: poolHoverBg ?? 'Hover.lowEmphasis',
                    }
                  : undefined
              return (
                <WrapItem key={p.label}>
                  {/* `as="button"` はクリックで追加できる時のみ — 編集モード中は
                      TagCloseTrigger 自体が button なので、TagRoot まで button に
                      すると button-in-button の不正 HTML になる。削除予定チップは
                      編集モード OFF でも button 化しない (上記 canAddByClick)。 */}
                  <TagRoot
                    as={canAddByClick ? 'button' : undefined}
                    textStyle="md"
                    textDecoration={isPendingRemoval ? 'line-through' : 'none'}
                    opacity={isPendingRemoval ? 0.5 : 1}
                    borderRadius="full"
                    _hover={poolHover}
                    // [design 差異] 付与済チップと同じ理由で md (14px)。 lineHeight も
                    // 揃える必要があるので textStyle を使う。 上の TagRoot のコメント参照。
                    cursor={
                      canAddByClick && !isAddDisabled ? 'pointer' : 'default'
                    }
                    aria-disabled={canAddByClick && isAddDisabled}
                    aria-label={
                      canAddByClick
                        ? t('common:tag.suggestAddAriaLabel', {
                            label: localizeBuiltinWorkflowText(p.label, t),
                          })
                        : undefined
                    }
                    backgroundColor={poolBg}
                    // 背景だけ prop 経由 (上の poolHover のコメント参照)。
                    onClick={
                      canAddByClick
                        ? () => {
                            handleAddSuggested(p)
                          }
                        : undefined
                    }
                    size="md"
                    style={poolStyleRest}
                    variant="subtle"
                  >
                    {canAddByClick && (
                      // ecloud 0.14.2 tag recipe は startElement スロットのスタイルも
                      // 欠落しているため (closeTrigger と同根)、 アイコンサイズを明示する。
                      <TagStartElement
                        alignItems="center"
                        display="inline-flex"
                        opacity={0.85}
                      >
                        <FaPlusRegular boxSize="10px" />
                      </TagStartElement>
                    )}
                    <TagLabel textStyle="md">
                      {localizeBuiltinWorkflowText(p.label, t)}
                    </TagLabel>
                    {showDeleteAffordance && (
                      <TagCloseTrigger
                        boxSize="14px"
                        opacity={0.75}
                        // 付与済チップの × と同じ (8080 `.case-tag-x:hover`)。
                        _hover={{ opacity: 1 }}
                        cursor="pointer"
                        aria-label={t(
                          isPendingRemoval
                            ? 'common:tag.poolUndoDeleteAriaLabel'
                            : 'common:tag.poolDeleteAriaLabel',
                          { label: localizeBuiltinWorkflowText(p.label, t) },
                        )}
                        css={{ '& svg': { width: '12px', height: '12px' } }}
                        onClick={() => {
                          togglePendingRemoval(p.label)
                        }}
                      />
                    )}
                  </TagRoot>
                </WrapItem>
              )
            })}
          </Wrap>
          {/* AC-TAG-009: 削除予定タグのうち参照件数 (X) > 0 のものだけ
              インラインアラートを出す (X=0 は削除予定状態のみで省略)。 */}
          {canEditPool &&
            (removedTagLabels ?? [])
              .map((label) => suggestedTags?.find((e) => e.label === label))
              .filter(
                (entry): entry is WorkflowTagPoolEntry =>
                  entry !== undefined && entry.referenceCount > 0,
              )
              .map((entry) => (
                <Alert
                  key={entry.label}
                  marginTop={2}
                  description={t('common:tag.pendingRemovalAlertDescription')}
                  status="warning"
                  title={t('common:tag.pendingRemovalAlertTitle', {
                    label: localizeBuiltinWorkflowText(entry.label, t),
                    count: entry.referenceCount,
                  })}
                />
              ))}
        </Box>
      )}
    </VStack>
  )
}
